import { useRef, useState } from "react";
import { Box, Card, CardContent, Divider, IconButton, Typography } from "@mui/material";
import { Edit, Delete, ChevronLeft, ChevronRight } from "@mui/icons-material";

export default function LibraryPlaylistsList({ playlists, onEdit, onDelete }) {
  const isCarousel = playlists.length > 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef(null);

  const renderPlaylistCard = (playlist) => (
    <Card
      key={playlist.id}
      sx={{
        backgroundColor: "#4b445b",
        height: 400,
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" color="secondary" sx={{ mb: 1, fontWeight: 700 }}>
            {playlist.name}
          </Typography>
          <Box>
            <IconButton color="secondary" onClick={() => onEdit(playlist)}>
              <Edit />
            </IconButton>
            <IconButton color="secondary" onClick={() => onDelete(playlist)}>
              <Delete />
            </IconButton>
          </Box>
        </Box>
        <Divider sx={{ mb: 1, backgroundColor: "rgba(255,255,255,0.2)" }} />
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            pr: 0.5,
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.35)",
              borderRadius: 999,
            },
          }}
        >
          <Typography variant="body2" color="secondary" sx={{ whiteSpace: "pre-line" }}>
            {(playlist.playlist || []).map((song) => song.item).join("\n")}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const updateActiveFromScroll = () => {
    if (!carouselRef.current) {
      return;
    }

    const container = carouselRef.current;
    const children = Array.from(container.children);
    if (!children.length) {
      return;
    }

    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    children.forEach((child, index) => {
      const childCenter = child.offsetLeft + child.clientWidth / 2;
      const distance = Math.abs(childCenter - containerCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex(closestIndex);
  };

  const scrollToIndex = (nextIndex) => {
    if (!carouselRef.current) {
      return;
    }

    const container = carouselRef.current;
    const maxIndex = playlists.length - 1;
    const safeIndex = Math.max(0, Math.min(nextIndex, maxIndex));
    const nextCard = container.children[safeIndex];
    if (!nextCard) {
      return;
    }

    // Center the selected card in viewport and clamp bounds
    // so navigation looks identical forward/backward.
    const targetLeft =
      nextCard.offsetLeft - (container.clientWidth - nextCard.clientWidth) / 2;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const clampedLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));

    container.scrollTo({
      left: clampedLeft,
      behavior: "smooth",
    });
  };

  if (!isCarousel) {
    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          {playlists.map((playlist) => (
            <Box
              key={playlist.id}
              sx={{
                flex: { xs: "0 0 76%", sm: "0 0 82%", md: "0 0 60%" },
                transform: "scale(1.01)",
                transformOrigin: "center center",
              }}
            >
              {renderPlaylistCard(playlist)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
        <IconButton
          onClick={() => scrollToIndex(activeIndex - 1)}
          disabled={activeIndex === 0}
          sx={{
            position: "absolute",
            left: { xs: 6, md: -46 },
            zIndex: 2,
            backgroundColor: "rgba(255,255,255,0.18)",
            color: "white",
            width: { xs: 30, md: 40 },
            height: { xs: 30, md: 40 },
            "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
          }}
        >
          <ChevronLeft sx={{ fontSize: { xs: 18, md: 24 } }} />
        </IconButton>
        <Box
          ref={carouselRef}
          onScroll={updateActiveFromScroll}
          sx={{
            display: "flex",
            gap: 2,
            width: "100%",
            overflowX: "auto",
            pb: 1,
            scrollSnapType: "x mandatory",
            scrollPaddingInline: { xs: "12%", md: "5%" },
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "@media (min-width: 900px)": {
              scrollPaddingInline: "20%",
            },
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {playlists.map((playlist, index) => (
            <Box
              key={playlist.id}
              sx={{
                flex: { xs: "0 0 76%", sm: "0 0 82%", md: "0 0 60%" },
                scrollSnapAlign: "center",
                transition:
                  "transform 0.28s ease, opacity 0.28s ease, filter 0.28s ease",
                transform: index === activeIndex ? "scale(1.01)" : "scale(0.9)",
                transformOrigin: "center center",
                opacity: index === activeIndex ? 1 : 0.5,
                filter:
                  index === activeIndex
                    ? "saturate(1)"
                    : "saturate(0.78) blur(0.3px)",
              }}
            >
              {renderPlaylistCard(playlist)}
            </Box>
          ))}
        </Box>
        <IconButton
          onClick={() => scrollToIndex(activeIndex + 1)}
          disabled={activeIndex === playlists.length - 1}
          sx={{
            position: "absolute",
            right: { xs: 6, md: -46 },
            zIndex: 2,
            backgroundColor: "rgba(255,255,255,0.18)",
            color: "white",
            width: { xs: 30, md: 40 },
            height: { xs: 30, md: 40 },
            "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
          }}
        >
          <ChevronRight sx={{ fontSize: { xs: 18, md: 24 } }} />
        </IconButton>
      </Box>
    </Box>
  );
}
